import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import KineticHeading from "@/components/KineticHeading";
import Reveal from "@/components/Reveal";
import SEO from "@/components/SEO";
import { LAB } from "@/data/lab";

const chapters = [
  {
    n: "01",
    title: "Our Legacy",
    body: "Lifeline Diagnostics has served the people of Jaysingpur since 2010 under the proprietorship of Dr. Neelam Attar (MBBS, MD Microbiology). What began as a single-doctor lab has become a trusted, ISO-certified centre for microbiology, biochemistry and pathology testing.",
  },
  {
    n: "02",
    title: "Precision",
    body: "Daily internal quality controls and yearly calibrations of machines and equipment aren't a checkbox — they're the discipline that lets us sign a report with confidence. Every reagent, every instrument, every technician is held to that standard.",
  },
  {
    n: "03",
    title: "People",
    body: "Three qualified technicians and support staff work in a spacious, aesthetically pleasant set-up designed to keep patients calm and workflows clean. Dr. Attar reviews microbiology culture and sensitivity results personally.",
  },
  {
    n: "04",
    title: "Beyond the Bench",
    body: "We work with hospitals for water and environmental testing, and provide guidance and training on infection control and antimicrobial stewardship practices — aligned with NABH requirements. Our job doesn't end at reporting; it extends to keeping hospitals safer.",
  },
];

function InteriorParallax() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start end", "end start"],
  });
  const y = useTransform(scrollYProgress, [0, 1], ["-8%", "10%"]);
  return (
    <div
      ref={ref}
      className="relative h-[70vh] md:h-[86vh] overflow-hidden"
      data-testid="about-interior-image"
    >
      <motion.img
        style={{ y, scale: 1.15 }}
        src={LAB.images.interior}
        alt="Inside Lifeline Diagnostics — equipment and analyzers"
        className="absolute inset-0 w-full h-full object-cover"
      />
      <div className="absolute inset-0 bg-navy/20 mix-blend-multiply" />
      <div className="absolute inset-0 spotlight" />
      <div className="absolute bottom-8 left-6 md:bottom-12 md:left-12 right-6 md:right-12 flex justify-between items-end text-white">
        <span className="font-mono-tag opacity-80">
          — Inside the laboratory
        </span>
        <span className="font-mono-tag opacity-80">Jaysingpur · 2010</span>
      </div>
    </div>
  );
}

export default function About() {
  return (
    <div data-testid="page-about">
      <SEO
        title="About Lifeline Diagnostics — Dr. Neelam Attar, ISO Certified Lab in Jaysingpur"
        description="Founded in 2010 by Dr. Neelam Attar (MBBS, MD Microbiology), Lifeline Diagnostics is an ISO-certified pathology and microbiology lab in Jaysingpur, Maharashtra. Daily quality controls, yearly calibrations, and NABH-aligned hospital infection-control guidance."
        keywords="about Lifeline Diagnostics, Dr Neelam Attar Jaysingpur, MD microbiology Jaysingpur, ISO certified lab Maharashtra, NABH infection control, antimicrobial stewardship, hospital water testing"
        path="/about"
      />
      {/* HERO */}
      <section className="pt-36 md:pt-44 pb-16 md:pb-24">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.6 }}
            className="flex items-center gap-3 mb-8 text-navy"
          >
            <span className="h-px w-10 bg-navy" />
            <span className="font-mono-tag">— About Lifeline</span>
          </motion.div>

          <KineticHeading
            lines={["A microbiologist's", <em key="e" className="italic font-medium text-cyan-brand">quiet laboratory.</em>]}
            className="font-display text-[14vw] md:text-[8.5vw] leading-[0.88] tracking-[-0.03em] text-navy"
          />

          <div className="mt-14 grid grid-cols-12 gap-8">
            <Reveal className="col-span-12 md:col-span-4 md:col-start-8">
              <p className="text-slate-600 leading-relaxed">
                Founded and led by Dr. Neelam Attar (MBBS, MD Microbiology),
                Lifeline Diagnostics has been a fixture of Jaysingpur's
                healthcare since 2010 — an ISO-certified centre for
                microbiology, biochemistry and pathology.
              </p>
            </Reveal>
          </div>
        </div>
      </section>

      {/* MANIFESTO CHAPTERS */}
      <section className="pb-24 md:pb-32">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          <div className="border-t hair">
            {chapters.map((ch, i) => (
              <Reveal
                key={ch.n}
                delay={i * 0.05}
                className="grid grid-cols-12 gap-8 py-14 md:py-20 border-b hair"
              >
                <div className="col-span-12 md:col-span-3">
                  <span className="font-mono-tag text-cyan-brand">
                    Chapter {ch.n}
                  </span>
                </div>
                <div className="col-span-12 md:col-span-6">
                  <h3 className="font-display text-4xl md:text-6xl leading-[1] tracking-tight text-navy">
                    {ch.title}
                  </h3>
                </div>
                <div className="col-span-12 md:col-span-3">
                  <p className="text-slate-600 leading-relaxed">{ch.body}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* INTERIOR PARALLAX */}
      <InteriorParallax />

      {/* DOCTOR / PROPRIETOR */}
      <section className="py-24 md:py-40 bg-paper">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          <div className="grid grid-cols-12 gap-10 items-start">
            <Reveal className="col-span-12 md:col-span-5">
              <p className="font-mono-tag text-cyan-brand">— Proprietor</p>
              <h2 className="mt-4 font-display text-4xl md:text-6xl leading-[1] tracking-tight text-navy">
                Dr. Neelam <em className="italic">Attar</em>
              </h2>
              <p className="mt-4 font-mono-tag text-slate-500">
                {LAB.proprietorCred}
              </p>
            </Reveal>

            <Reveal delay={0.15} className="col-span-12 md:col-span-7 space-y-6 text-slate-700 leading-relaxed">
              <p>
                A trained microbiologist, Dr. Attar personally oversees the
                microbiology bench — from Gram-staining and cultures to
                antibiotic sensitivity reports that clinicians rely on.
              </p>
              <p>
                Her work extends beyond the lab: she consults hospitals
                on infection control, antimicrobial stewardship and
                NABH-aligned environmental testing — quietly shaping how
                safely local hospitals treat their patients.
              </p>
              <div className="pt-6 border-t hair grid grid-cols-2 gap-6 max-w-xl">
                <div>
                  <span className="font-display text-3xl text-navy">15+</span>
                  <p className="font-mono-tag text-slate-500 mt-1">Years in practice</p>
                </div>
                <div>
                  <span className="font-display text-3xl text-navy">MD</span>
                  <p className="font-mono-tag text-slate-500 mt-1">Microbiology</p>
                </div>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* VALUE STRIP */}
      <section className="py-24 md:py-32 bg-navy text-white grain">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          <div className="grid grid-cols-12 gap-8">
            <Reveal className="col-span-12 md:col-span-5">
              <p className="font-mono-tag text-cyan-brand">— Standards</p>
              <h3 className="mt-4 font-display text-4xl md:text-5xl leading-[1] tracking-tight">
                Quality controls, done <em className="italic">daily</em>.
              </h3>
            </Reveal>
            <div className="col-span-12 md:col-span-7 grid sm:grid-cols-2 gap-8 text-white/80">
              {[
                ["ISO Certified", "Independent third-party quality audit."],
                ["Daily QC", "Internal reference runs, every working day."],
                ["Yearly Calibration", "All analyzers calibrated against reference standards."],
                ["NABH Aligned", "Hospital infection-control guidance and training."],
              ].map(([k, v]) => (
                <Reveal key={k} className="border-t border-white/15 pt-6">
                  <div className="font-display text-2xl text-white">{k}</div>
                  <p className="mt-2 text-white/70">{v}</p>
                </Reveal>
              ))}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
